import moment from "moment";
import {BufferWriter, Writer} from "protobufjs";

import {Company} from "../../../domain/model/company";
import {
    ICompanyRepository,
    TCompanyIDWithProjectID,
    TCompanyWithProjectID,
    TUpdateCompanyParams,
} from "../../../domain/repository/company_repository";

import {IApiClient} from "../client";
import {api, entity} from "../rpc/api";
import {getToken} from "./bind_token";

import {FrontDate} from "../../../domain/model/frontDate";
import {CompanyConvertResponse, CompanyConvertResponseList,} from "./company_api_convert";
import CompanyWithProjectIDList = api.CompanyWithProjectIDList;
import CompanyWithProjectID = api.CompanyWithProjectID;
import CompanyID = entity.CompanyID;
import CompanyIDList = entity.CompanyIDList;
import CompanyList = entity.CompanyList;
import CompanyIDWithProjectIDList = api.CompanyIDWithProjectIDList;
import CompanyIDWithProjectID = api.CompanyIDWithProjectID;
import UpdateCompanyRequest = api.UpdateCompanyRequest;

class CompanyAPI implements ICompanyRepository {
    // establishedAt: Frontend => BackEnd における型変換
    private static establishedAtConvertToString = (
        frontDate: FrontDate | undefined
    ): string => {
        if (!frontDate) {
            return ``;
        } else {
            return frontDate.year === "" && frontDate.month === "" && frontDate.day
                ? ``
                : moment()
                    .year(Number(frontDate.year.toString()))
                    .month(Number(frontDate.month.toString()) - 1)
                    .date(Number(frontDate.day.toString()))
                    .format("YYYY-MM-DD")
                    .toString();
        }
    };

    constructor(readonly apiClient: IApiClient) {
    }

    public getAllByIDs(ids: string[]): Promise<Company[]> {
        return new Promise<Company[]>((resolve, reject) => {
            getToken()
                .then((token: string): Promise<Uint8Array> => {
                    const req: CompanyIDList = new CompanyIDList();
                    req.ids = ids;
                    const writer: BufferWriter | Writer = Writer.create();
                    return this.apiClient.postWithToken(
                        "/api.CompanyService/GetAllByIDs",
                        token,
                        CompanyIDList.encode(req, writer).finish()
                    );
                })
                .then((binary: Uint8Array): void => {
                    const res: CompanyList = CompanyList.decode(binary);
                    // BE=>FE 型変換
                    const resConverted: CompanyConvertResponseList =
                        CompanyConvertResponseList.from(res);
                    const items: Company[] = resConverted.items.map(
                        (item: CompanyConvertResponse): Company => {
                            return Company.from(item);
                        }
                    );
                    resolve(items);
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    }

    public getAllByIDsWithMask(
        ids: TCompanyIDWithProjectID[]
    ): Promise<TCompanyWithProjectID[]> {
        return new Promise<TCompanyWithProjectID[]>((resolve, reject) => {
            getToken()
                .then((token: string): Promise<Uint8Array> => {
                    const req: CompanyIDWithProjectIDList =
                        new CompanyIDWithProjectIDList();
                    req.items = ids;
                    const writer: BufferWriter | Writer = Writer.create();
                    return this.apiClient.postWithToken(
                        "/api.CompanyService/GetAllByIDsWithMask",
                        token,
                        CompanyIDWithProjectIDList.encode(req, writer).finish()
                    );
                })
                .then((binary: Uint8Array): void => {
                    const res: CompanyWithProjectIDList =
                        CompanyWithProjectIDList.decode(binary);
                    // BE=>FE 型変換
                    const items: TCompanyWithProjectID[] = res.items.map(
                        (item: CompanyWithProjectID) => {
                            const resConverted: CompanyConvertResponse =
                                CompanyConvertResponse.from(item.company as entity.Company);
                            const resCompany: Company = Company.from(resConverted);
                            return {
                                projectID: item.projectID,
                                company: resCompany,
                            };
                        }
                    );

                    resolve(items);
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    }

    public get(companyID: string): Promise<Company> {
        return new Promise<Company>((resolve, reject) => {
            getToken()
                .then((token: string): Promise<Uint8Array> => {
                    const req: CompanyID = new CompanyID();
                    req.id = companyID;
                    const writer: BufferWriter | Writer = Writer.create();
                    return this.apiClient.postWithToken(
                        "/api.CompanyService/Get",
                        token,
                        CompanyID.encode(req, writer).finish()
                    );
                })
                .then((binary: Uint8Array): void => {
                    const res: entity.Company = entity.Company.decode(binary);
                    // BE=>FE 型変換
                    const resConverted: CompanyConvertResponse =
                        CompanyConvertResponse.from(res);
                    const item: Company = Company.from(resConverted);
                    resolve(item);
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    }

    public getWithMask(companyID: string, projectID: string): Promise<Company> {
        return new Promise<Company>((resolve, reject) => {
            getToken()
                .then((token: string): Promise<Uint8Array> => {
                    const req: CompanyIDWithProjectID = new CompanyIDWithProjectID();
                    req.companyID = companyID;
                    req.projectID = projectID;
                    const writer: BufferWriter | Writer = Writer.create();
                    return this.apiClient.postWithToken(
                        "/api.CompanyService/GetWithMask",
                        token,
                        CompanyIDWithProjectID.encode(req, writer).finish()
                    );
                })
                .then((binary: Uint8Array): void => {
                    const res: entity.Company = entity.Company.decode(binary);
                    // BE=>FE 型変換
                    const resConverted: CompanyConvertResponse =
                        CompanyConvertResponse.from(res);
                    const item: Company = Company.from(resConverted);
                    resolve(item);
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    }

    public update(updateCompanyParams: TUpdateCompanyParams): Promise<Company> {
        return new Promise<Company>((resolve, reject) => {
            getToken()
                .then((token: string): Promise<Uint8Array> => {
                    // establishedAt: Frontend => BackEnd における型変換
                    const establishedAt_string: string =
                        CompanyAPI.establishedAtConvertToString(
                            updateCompanyParams.establishedAt
                        );

                    const req: UpdateCompanyRequest = new UpdateCompanyRequest();
                    req.name = updateCompanyParams.name;
                    req.logoURL = updateCompanyParams.logoURL;
                    req.webURL = updateCompanyParams.webURL;
                    req.establishedAt = establishedAt_string;
                    req.postalCode = updateCompanyParams.postalCode;
                    req.place = updateCompanyParams.place;
                    req.businesses = updateCompanyParams.businesses;
                    req.representativeName = updateCompanyParams.representativeName;
                    req.capitalStock = updateCompanyParams.capitalStock;
                    req.employeeScale = updateCompanyParams.employeeScale;
                    req.introduction = updateCompanyParams.introduction;
                    req.accordingCompanyName = updateCompanyParams.accordingCompanyName;
                    req.accordingCompanyPostalCode =
                        updateCompanyParams.accordingCompanyPostalCode;
                    req.accordingCompanyAddress =
                        updateCompanyParams.accordingCompanyAddress;
                    req.invoiceDepartmentName = updateCompanyParams.invoiceDepartmentName;
                    const writer: BufferWriter | Writer = Writer.create();
                    return this.apiClient.postWithToken(
                        "/api.CompanyService/Update",
                        token,
                        UpdateCompanyRequest.encode(req, writer).finish()
                    );
                })
                .then((binary: Uint8Array): void => {
                    const res: entity.Company = entity.Company.decode(binary);
                    // BE=>FE 型変換
                    const resConverted: CompanyConvertResponse =
                        CompanyConvertResponse.from(res);
                    const item: Company = Company.from(resConverted);
                    resolve(item);
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    }
}

const createCompanyAPI = (apiClient: IApiClient): ICompanyRepository => {
    return new CompanyAPI(apiClient);
};

export {createCompanyAPI};



// WEBPACK FOOTER //
// ./src/infra/api/service/company_api.ts